import * as React from 'react';
import {Avatar, Button, Divider, IconButton, ListItemIcon, Menu, MenuItem} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonIcon from '@mui/icons-material/Person';
import {useAuth} from '../../auth/AuthContext';
import styles from './UserMenu.module.scss';

interface UserMenuProps {
    /** `full` for the drawer footer, `compact` for the mobile floating button. */
    variant?: 'full' | 'compact';
}

export const UserMenu: React.FC<UserMenuProps> = ({variant = 'full'}) => {
    const {user, status, promptLogin, signOut} = useAuth();
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);

    if (status === 'loading') {
        return variant === 'full' ? <div className={styles.userMenu__skeleton}/> : null;
    }

    const displayName = user
        ? user.displayName || user.email?.split('@')[0] || 'Account'
        : null;
    const initial = displayName?.charAt(0).toUpperCase();

    const handleSignOut = async () => {
        setAnchor(null);
        await signOut();
    };

    // Guest — compact shows an icon button, full shows a labeled button.
    if (!user) {
        if (variant === 'compact') {
            return (
                <IconButton
                    onClick={() => promptLogin()}
                    className={styles.userMenu__compactGuest}
                    aria-label="Sign in"
                >
                    <LoginIcon/>
                </IconButton>
            );
        }
        return (
            <Button
                fullWidth
                variant="outlined"
                startIcon={<LoginIcon/>}
                onClick={() => promptLogin()}
                className={styles.userMenu__signInBtn}
            >
                Sign in
            </Button>
        );
    }

    // Signed in — compact shows avatar only, full shows avatar + name/email.
    const triggerClass = variant === 'compact' ? styles.userMenu__compactTrigger : styles.userMenu__trigger;

    return (
        <>
            <button
                type="button"
                className={triggerClass}
                onClick={e => setAnchor(e.currentTarget)}
                aria-label="Account menu"
            >
                <Avatar
                    src={user.photoURL || undefined}
                    alt={displayName!}
                    className={styles.userMenu__avatar}
                >
                    {initial}
                </Avatar>
                {variant === 'full' && (
                    <div className={styles.userMenu__info}>
                        <div className={styles.userMenu__name}>{displayName}</div>
                        {user.email && <div className={styles.userMenu__email}>{user.email}</div>}
                    </div>
                )}
            </button>

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{
                    vertical: variant === 'compact' ? 'bottom' : 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: variant === 'compact' ? 'top' : 'bottom',
                    horizontal: 'right',
                }}
            >
                <MenuItem disabled className={styles.userMenu__menuHeader}>
                    <ListItemIcon><PersonIcon fontSize="small"/></ListItemIcon>
                    <span>
                        Signed in as <strong>{displayName}</strong>
                    </span>
                </MenuItem>
                <Divider/>
                <MenuItem onClick={handleSignOut}>
                    <ListItemIcon><LogoutIcon fontSize="small"/></ListItemIcon>
                    Sign out
                </MenuItem>
            </Menu>
        </>
    );
};
